import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Printer, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../lib/backend';
import usePageMeta from '../hooks/usePageMeta';

const money = (n) => `${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const ItemsTable = ({ o }) => (
  <table className="w-full text-sm" data-testid="offer-items-table">
    <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
      <th className="py-2">Descrizione</th><th className="py-2 text-right w-16">Q.tà</th><th className="py-2 text-right w-28">Prezzo</th><th className="py-2 text-right w-28">Importo</th>
    </tr></thead>
    <tbody>
      {o.items.map((i, k) => (
        <tr key={k} className="border-b border-slate-100">
          <td className="py-2.5 text-slate-800">{i.description}</td>
          <td className="py-2.5 text-right text-slate-600">{i.qty}</td>
          <td className="py-2.5 text-right text-slate-600">{Number(i.unit_price) === 0 ? 'Incluso' : money(i.unit_price)}</td>
          <td className="py-2.5 text-right text-slate-800">{money(i.qty * i.unit_price)}</td>
        </tr>
      ))}
    </tbody>
    <tfoot className="text-sm">
      {o.discount > 0 && <tr><td colSpan={3} className="pt-3 text-right text-slate-500">Sconto {o.discount_pct}%</td><td className="pt-3 text-right text-slate-700">−{money(o.discount)}</td></tr>}
      <tr><td colSpan={3} className="pt-1 text-right text-slate-500">Imponibile</td><td className="pt-1 text-right text-slate-700">{money(o.taxable)}</td></tr>
      <tr><td colSpan={3} className="pt-1 text-right text-slate-500">IVA {o.vat_pct}%</td><td className="pt-1 text-right text-slate-700">{money(o.vat)}</td></tr>
      <tr><td colSpan={3} className="pt-3 text-right font-bold text-slate-900">Totale</td><td className="pt-3 text-right font-black text-lg text-slate-900" data-testid="offer-public-total">{money(o.total)}</td></tr>
    </tfoot>
  </table>
);

const Decision = ({ o, token, onDone }) => {
  const [name, setName] = useState(o.client_name || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const act = async (kind) => {
    let body = { name };
    if (kind === 'decline') {
      const reason = window.prompt('Vuoi dirci il motivo? (facoltativo)', '');
      if (reason === null) return;
      body = { reason };
    }
    setBusy(true); setErr('');
    try { const r = await axios.post(`${BACKEND_URL}/api/offers/public/${token}/${kind}`, body); onDone(r.data.status, name.trim()); }
    catch (e) { setErr(e.response?.data?.detail || 'Operazione non riuscita'); }
    setBusy(false);
  };
  if (o.status === 'accepted') return <p className="flex items-center gap-2 text-emerald-700 font-semibold" data-testid="offer-accepted-msg"><CheckCircle2 className="w-5 h-5" /> Proposta accettata{o.accepted_name ? ` da ${o.accepted_name}` : ''}. Ti contatteremo a breve per i prossimi passi.</p>;
  if (o.status === 'declined') return <p className="flex items-center gap-2 text-slate-600" data-testid="offer-declined-msg"><XCircle className="w-5 h-5" /> Proposta rifiutata. Grazie per averci risposto.</p>;
  return (
    <div className="space-y-3 print:hidden" data-testid="offer-decision">
      <p className="text-sm text-slate-600">Per confermare, scrivi nome e cognome e premi "Accetto la proposta".</p>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome" className="flex-1 min-w-[220px] px-4 py-3 border border-slate-300 rounded-full text-slate-900" data-testid="offer-accept-name" />
        <button onClick={() => act('accept')} disabled={busy || name.trim().length < 2} className="px-6 py-3 rounded-full bg-slate-900 text-amber-400 font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2" data-testid="offer-accept-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Accetto la proposta
        </button>
      </div>
      <button onClick={() => act('decline')} disabled={busy} className="text-xs text-slate-500 hover:text-slate-800 underline" data-testid="offer-decline-btn">Non mi interessa</button>
      {err && <p className="text-sm text-rose-600" data-testid="offer-error">{err}</p>}
    </div>
  );
};

export default function OfferPublicPage() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);
  usePageMeta({ title: 'La tua proposta — VocalFitness', noindex: true });
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/offers/public/${token}`).then((r) => setO(r.data)).catch(() => setMissing(true));
  }, [token]);

  if (missing) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-600" data-testid="offer-missing">Proposta non trovata o non più disponibile.</div>;
  if (!o) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4 print:bg-white print:py-0" data-testid="offer-public-page">
      <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden print:shadow-none print:rounded-none">
        <div className="bg-slate-950 px-8 py-7 flex items-center justify-between">
          <div>
            <p className="text-amber-400 font-black text-2xl tracking-tight">VocalFitness</p>
            <p className="text-slate-400 text-xs uppercase tracking-[0.25em] mt-1">Proposta {o.number}</p>
          </div>
          <button onClick={() => window.print()} className="print:hidden flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/20" data-testid="offer-print-btn">
            <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Scarica PDF</span>
          </button>
        </div>
        <div className="px-8 py-8 space-y-7">
          <div className="flex flex-wrap justify-between gap-4 text-sm">
            <div><p className="text-[11px] uppercase tracking-widest text-slate-400">Per</p><p className="font-semibold text-slate-900">{o.client_name}</p>{o.client_company && <p className="text-slate-600">{o.client_company}</p>}</div>
            <div className="sm:text-right"><p className="text-[11px] uppercase tracking-widest text-slate-400">Data</p><p className="text-slate-800">{date(o.created_at)}</p><p className="text-[11px] text-slate-500 mt-1">Valida fino al {date(o.valid_until)}</p></div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight" data-testid="offer-public-title">{o.title}</h1>
          <div className="prose prose-slate max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: o.intro }} />
          <ItemsTable o={o} />
          {o.terms && <p className="text-xs text-slate-500 border-t border-slate-100 pt-4">{o.terms}</p>}
          <div className="border-t border-slate-100 pt-6"><Decision o={o} token={token} onDone={(status, typed) => setO({ ...o, status, accepted_name: status === 'accepted' ? typed : '' })} /></div>
        </div>
      </div>
    </div>
  );
}
